import ReactGA from 'react-ga4';

export type AnalyticsInitOptions = {
  /** GA4 measurement ID (e.g. G-XXXX). Missing/empty → full no-op. */
  measurementId?: string;
  /** When true, sets gtag `debug_mode` (useful for GA4 DebugView during local dev). */
  debug?: boolean;
};

/**
 * GA4 custom event params. Primitives only (GA4-friendly); undefined keys
 * are omitted at the trackEvent boundary.
 */
export type AnalyticsEventParams = Record<
  string,
  string | number | boolean | undefined
>;

let initialized = false;
let enabled = false;

function omitUndefined(
  params?: AnalyticsEventParams
): Record<string, string | number | boolean> | undefined {
  if (!params) {
    return undefined;
  }

  const cleaned: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) {
      cleaned[key] = value;
    }
  }
  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
}

/**
 * Idempotent. Safe at boot; never throws into app startup.
 * Missing/empty measurement ID → full no-op for all track calls.
 */
export function initAnalytics(options: AnalyticsInitOptions): void {
  if (initialized) {
    return;
  }

  const measurementId = options.measurementId?.trim();
  if (!measurementId) {
    return;
  }

  try {
    ReactGA.initialize(measurementId, {
      gtagOptions: {
        send_page_view: false,
        ...(options.debug ? { debug_mode: true } : {}),
      },
    });
    enabled = true;
    initialized = true;
  } catch (error) {
    console.error('Failed to initialize analytics', error);
    enabled = false;
  }
}

/**
 * GA4 custom event. No-op until initialized (and when init had no ID).
 *
 * - `name`: snake_case preferred (`embedded_opened`, `embedded_message_sent`)
 * - `params`: primitive values only; omit undefined keys at the boundary
 */
export function trackEvent(name: string, params?: AnalyticsEventParams): void {
  if (!enabled) {
    return;
  }

  try {
    const cleaned = omitUndefined(params);
    if (cleaned) {
      ReactGA.event(name, cleaned);
    } else {
      ReactGA.event(name);
    }
  } catch (error) {
    console.error('Failed to track event', error);
  }
}

/** @internal Resets module state between unit tests. */
export function resetAnalyticsStateForTests(): void {
  initialized = false;
  enabled = false;
  const ga = ReactGA as typeof ReactGA & { reset?: () => void };
  ga.reset?.();
}
